import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, IconButton, InputLabel, MenuItem,
  Paper, Select, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, ToggleButton,
  ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import { Description, PictureAsPdf, Refresh, Sync, Visibility, Edit, Save, Replay } from '@mui/icons-material';
import ReorderDialog from './ReorderDialog';
import PoLinesView from './PoLinesView';
import { orderProcessorAPI } from '../../services/api';
import { downloadBlob, errMsg, EVENT_LABEL, fmtDateTime, inr, isoDaysAgo, num, PO_NEXT, StatusChip } from './common';

interface Props {
  runId?: number;          // when set: only this run's POs, no date filters
  storeId?: number | null; // default store filter for the global view
}

const PurchaseOrders: React.FC<Props> = ({ runId, storeId }) => {
  const [rows, setRows] = useState<any[]>([]);
  const [from, setFrom] = useState(isoDaysAgo(90));
  const [to, setTo] = useState(isoDaysAgo(0));
  const [status, setStatus] = useState('');
  const [storeOnly, setStoreOnly] = useState(true);
  const [detail, setDetail] = useState<any | null>(null);
  const [edit, setEdit] = useState<Record<number, { received_qty: string; line_status: string }>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [reorderPo, setReorderPo] = useState<number | null>(null);
  // Global PO tab: By PO | Optimizer items | Customer orders (kept in the URL); a run's PO step is always "By PO"
  const [params, setParams] = useSearchParams();
  const view = runId ? 'po' : (params.get('view') || 'po');
  const setView = (v: string) => { const p = new URLSearchParams(params); if (v === 'po') p.delete('view'); else p.set('view', v); setParams(p); };
  const [notice, setNotice] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [bulk, setBulk] = useState<{ status: string; label: string } | null>(null);

  const load = useCallback(async () => {
    try {
      const f: any = runId ? { run_id: runId } : { from, to, status: status || undefined, store_id: storeOnly && storeId ? storeId : undefined };
      setRows(await orderProcessorAPI.listPurchaseOrders(f));
    } catch (e) { setError(errMsg(e)); }
  }, [runId, from, to, status, storeOnly, storeId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSelected((cur) => new Set(Array.from(cur).filter((id) => rows.some((r) => r.id === id)))); }, [rows]);

  const openDetail = async (id: number) => {
    try { setDetail(await orderProcessorAPI.getPurchaseOrder(id)); setEdit({}); } catch (e) { setError(errMsg(e)); }
  };

  const act = async (fn: () => Promise<any>, refreshDetail?: number) => {
    setBusy(true); setError(null);
    try {
      await fn();
      await load();
      if (refreshDetail) await openDetail(refreshDetail);
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };

  const dl = async (po: any, kind: 'csv' | 'pdf') => {
    try {
      const blob = kind === 'csv' ? await orderProcessorAPI.exportPoCsv(po.id) : await orderProcessorAPI.exportPoPdf(po.id);
      const safe = String(po.distributor_name || 'distributor').trim().replace(/[/\\]/g, '_');
      downloadBlob(blob, `${po.po_number}_${safe}.${kind}`);
    } catch (e) { setError(errMsg(e)); }
  };

  const selectedRows = rows.filter((r) => selected.has(r.id));
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const toggle = (id: number) => setSelected((cur) => { const n = new Set(cur); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const BULK_ACTIONS = [
    { status: 'sent', label: 'Mark sent', color: 'primary' as const },
    { status: 'closed', label: 'Close', color: 'inherit' as const },
    { status: 'cancelled', label: 'Cancel', color: 'error' as const },
    { status: 'draft', label: 'Reopen', color: 'inherit' as const },
  ];
  const eligibleFor = (status: string) => selectedRows.filter((r) => (PO_NEXT[r.status] || []).includes(status));

  const runBulk = async () => {
    if (!bulk) return;
    const ids = eligibleFor(bulk.status).map((r) => r.id);
    setBusy(true); setError(null);
    try {
      const res = await orderProcessorAPI.bulkPurchaseOrderStatus(ids, bulk.status);
      const skippedNow = selectedRows.length - ids.length + res.skipped.length;
      setNotice(`${bulk.label}: ${res.done.length} PO(s) updated${skippedNow ? ` · ${skippedNow} skipped (not applicable)` : ''}.`
        + (res.skipped.length ? ` ${res.skipped.map((x: any) => `${x.po_number || x.id}: ${x.reason}`).join('; ')}` : ''));
      setBulk(null); setSelected(new Set());
      await load();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };

  const bulkMatch = async () => {
    const ids = selectedRows.filter((r) => ['sent', 'partially_received'].includes(r.status)).map((r) => r.id);
    setBusy(true); setError(null);
    try {
      for (const id of ids) {
        // eslint-disable-next-line no-await-in-loop
        await orderProcessorAPI.matchReceipts(id);
      }
      setNotice(`Receipts checked for ${ids.length} sent PO(s)${selectedRows.length - ids.length ? ` · ${selectedRows.length - ids.length} skipped (not sent)` : ''}.`);
      await load();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };

  const bulkDownload = async () => {
    for (const po of selectedRows.filter((r) => r.status !== 'cancelled')) {
      // eslint-disable-next-line no-await-in-loop
      await dl(po, 'csv');
    }
  };

  const downloadAll = async () => {
    for (const po of rows.filter((r) => r.status !== 'cancelled')) {
      // sequential so the browser doesn't drop downloads
      // eslint-disable-next-line no-await-in-loop
      await dl(po, 'csv');
    }
  };

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}
      {!runId && (
        <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, v) => { if (v) setView(v); }} sx={{ mb: 2 }}>
          <ToggleButton value="po">By PO</ToggleButton>
          <ToggleButton value="optimizer">Optimizer items</ToggleButton>
          <ToggleButton value="customer">Customer orders</ToggleButton>
        </ToggleButtonGroup>
      )}

      {view !== 'po' && <PoLinesView key={view} view={view as 'optimizer' | 'customer'} storeId={storeId} onOpenPo={openDetail} />}

      {view === 'po' && (<>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 1.5 }} alignItems={{ md: 'center' }} flexWrap="wrap" useFlexGap>
        {!runId && (
          <>
            <TextField size="small" type="date" label="From" value={from} onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField size="small" type="date" label="To" value={to} onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="draft,sent,partially_received">Open (draft / sent / partial)</MenuItem>
                {Object.keys(PO_NEXT).map((s) => <MenuItem key={s} value={s}>{s.replace(/_/g, ' ')}</MenuItem>)}
              </Select>
            </FormControl>
            {storeId && <Button size="small" variant={storeOnly ? 'contained' : 'outlined'} onClick={() => setStoreOnly(!storeOnly)}>{storeOnly ? 'This store' : 'All stores'}</Button>}
          </>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" startIcon={<Refresh />} onClick={load}>Refresh</Button>
        <Button size="small" startIcon={<Sync />} disabled={busy} onClick={() => act(() => orderProcessorAPI.matchAllReceipts(storeOnly && storeId ? storeId : undefined))}>Match receipts</Button>
        {runId && rows.length > 0 && <Button size="small" startIcon={<Description />} onClick={downloadAll}>Download all CSVs</Button>}
      </Stack>

      {selectedRows.length > 0 && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', bgcolor: '#f5f9ff' }}>
          <Typography variant="body2" sx={{ mr: 1 }}><b>{selectedRows.length}</b> PO(s) selected</Typography>
          {BULK_ACTIONS.map((a) => {
            const n = eligibleFor(a.status).length;
            return (
              <Button key={a.status} size="small" variant={a.status === 'sent' ? 'contained' : 'outlined'} color={a.color} disabled={busy || n === 0}
                onClick={() => setBulk({ status: a.status, label: a.label })}>
                {a.label} ({n})
              </Button>
            );
          })}
          <Button size="small" startIcon={<Sync />} disabled={busy || !selectedRows.some((r) => ['sent', 'partially_received'].includes(r.status))} onClick={bulkMatch}>Match receipts</Button>
          <Button size="small" startIcon={<Description />} disabled={busy} onClick={bulkDownload}>Download CSVs</Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button size="small" onClick={() => setSelected(new Set())}>Clear</Button>
        </Paper>
      )}

      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: runId ? undefined : '65vh' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox size="small" checked={allSelected} indeterminate={!allSelected && selectedRows.length > 0}
                  onChange={() => setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.id)))} />
              </TableCell>
              <TableCell>PO</TableCell>
              {!runId && <TableCell>Store</TableCell>}
              <TableCell>Distributor</TableCell>
              <TableCell align="right">Items</TableCell>
              <TableCell align="right">Strips</TableCell>
              <TableCell align="right">Est. value</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Receipt</TableCell>
              <TableCell>Created / sent</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((po) => (
              <TableRow key={po.id} hover selected={selected.has(po.id)}>
                <TableCell padding="checkbox"><Checkbox size="small" checked={selected.has(po.id)} onChange={() => toggle(po.id)} /></TableCell>
                <TableCell><b>{po.po_number}</b><Typography variant="caption" display="block" color="text.secondary">{po.run_number}</Typography></TableCell>
                {!runId && <TableCell>{po.store_name}</TableCell>}
                <TableCell>{po.distributor_name}</TableCell>
                <TableCell align="right">{po.total_items}</TableCell>
                <TableCell align="right">{num(po.total_strips, 0)}</TableCell>
                <TableCell align="right">{inr(po.estimated_value)}</TableCell>
                <TableCell><StatusChip status={po.status} /></TableCell>
                <TableCell>
                  <Typography variant="caption">
                    {po.lines_received} recv · {po.lines_partial} partial · {po.lines_not_supplied} not supplied
                  </Typography>
                </TableCell>
                <TableCell><Typography variant="caption">{fmtDateTime(po.created_at)}{po.sent_at ? ` / ${fmtDateTime(po.sent_at)}` : ''}</Typography></TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <Tooltip title="Details"><IconButton size="small" onClick={() => openDetail(po.id)}><Visibility fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="CSV (medicine, pack, strips)"><IconButton size="small" onClick={() => dl(po, 'csv')}><Description fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="PDF"><IconButton size="small" onClick={() => dl(po, 'pdf')}><PictureAsPdf fontSize="small" /></IconButton></Tooltip>
                  {po.status === 'draft' && (
                    <Button size="small" variant="contained" disabled={busy} sx={{ ml: 1 }} onClick={() => act(() => orderProcessorAPI.setPurchaseOrderStatus(po.id, 'sent'))}>Mark sent</Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!rows.length && <TableRow><TableCell colSpan={11}><Typography variant="body2" color="text.secondary">No purchase orders in this range.</Typography></TableCell></TableRow>}
          </TableBody>
        </Table>
      </TableContainer>
      </>)}

      <Dialog open={!!bulk} onClose={() => (busy ? undefined : setBulk(null))} maxWidth="sm" fullWidth>
        {bulk && (() => {
          const ok = eligibleFor(bulk.status); const skip = selectedRows.length - ok.length;
          return (
            <>
              <DialogTitle>{bulk.label} — {ok.length} PO(s)?</DialogTitle>
              <DialogContent dividers>
                <Typography variant="body2" gutterBottom>
                  {ok.map((r) => `${r.po_number} (${r.distributor_name})`).slice(0, 12).join(', ')}{ok.length > 12 ? ` … +${ok.length - 12} more` : ''}
                </Typography>
                {skip > 0 && <Alert severity="info" sx={{ mt: 1 }}>{skip} selected PO(s) will be skipped — this change doesn’t apply to their current status.</Alert>}
                {bulk.status === 'sent' && <Alert severity="warning" sx={{ mt: 1 }}>Only mark as sent once the POs have actually been sent to the distributors. Linked customer orders stay “pending” — mark them ordered from the PO when ready.</Alert>}
                {bulk.status === 'cancelled' && <Alert severity="error" sx={{ mt: 1 }}>Cancelled POs are excluded from receipts and duplicates; they can be reopened later.</Alert>}
                {bulk.status === 'closed' && <Alert severity="info" sx={{ mt: 1 }}>Closing marks lines still pending as “not supplied”.</Alert>}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setBulk(null)} disabled={busy}>Cancel</Button>
                <Button variant="contained" color={bulk.status === 'cancelled' ? 'error' : 'primary'} disabled={busy || ok.length === 0} onClick={runBulk}>
                  {bulk.label} {ok.length} PO(s)
                </Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="lg" fullWidth>
        {detail && (
          <>
            <DialogTitle>
              {detail.po_number} — {detail.distributor_name} <StatusChip status={detail.status} />
              <Typography variant="body2" color="text.secondary">
                {detail.store_name} · {detail.run_number} · created {fmtDateTime(detail.created_at)}{detail.sent_at ? ` · sent ${fmtDateTime(detail.sent_at)}` : ''}
                {detail.last_matched_at ? ` · receipts checked ${fmtDateTime(detail.last_matched_at)}` : ''}
              </Typography>
            </DialogTitle>
            <DialogContent dividers>
              <Alert severity="info" sx={{ mb: 2 }}>
                Receipts are matched automatically from uploaded Purchase data: same store and distributor, any duplicate master of the item,
                dated from the send date up to 7 days later. Lines still unmatched after 7 days become “not supplied”. Edit a line to override it.
              </Alert>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Medicine</TableCell>
                      <TableCell>Pack</TableCell>
                      <TableCell align="right">Strips</TableCell>
                      <TableCell align="right">Loose qty</TableCell>
                      <TableCell align="right">Received</TableCell>
                      <TableCell>Line status</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detail.items.map((it: any) => {
                      const e = edit[it.id];
                      return (
                        <TableRow key={it.id}>
                          <TableCell>
                            {it.medicine_name}<Typography variant="caption" display="block" color="text.secondary">{it.manufacturer_name}</Typography>
                            {it.reordered_to_po_number && <Typography variant="caption" display="block" color="warning.main">↪ re-ordered on {it.reordered_to_po_number}</Typography>}
                            {it.reorder_of_po_number && <Typography variant="caption" display="block" color="info.main">re-order of {it.reorder_of_po_number}</Typography>}
                          </TableCell>
                          <TableCell>{it.pack_size || '—'}</TableCell>
                          <TableCell align="right">{num(it.strip_qty, 0)}</TableCell>
                          <TableCell align="right">{num(it.loose_qty, 0)}</TableCell>
                          <TableCell align="right">
                            {e ? <TextField size="small" type="number" value={e.received_qty} inputProps={{ style: { width: 70, textAlign: 'right' } }}
                              onChange={(ev) => setEdit({ ...edit, [it.id]: { ...e, received_qty: ev.target.value } })} /> : num(it.received_qty, 0)}
                          </TableCell>
                          <TableCell>
                            {e ? (
                              <Select size="small" value={e.line_status} onChange={(ev) => setEdit({ ...edit, [it.id]: { ...e, line_status: ev.target.value } })}>
                                {['pending', 'received', 'partial', 'not_supplied'].map((s) => <MenuItem key={s} value={s}>{s.replace('_', ' ')}</MenuItem>)}
                              </Select>
                            ) : <><StatusChip status={it.line_status} />{it.manual_override && <Typography variant="caption"> (manual)</Typography>}</>}
                          </TableCell>
                          <TableCell>
                            {e ? (
                              <IconButton size="small" disabled={busy} onClick={() => act(() => orderProcessorAPI.overrideReceipt(detail.id, it.id, { received_qty: Number(e.received_qty), line_status: e.line_status }), detail.id)}><Save fontSize="small" /></IconButton>
                            ) : (
                              <IconButton size="small" onClick={() => setEdit({ ...edit, [it.id]: { received_qty: String(it.received_qty ?? 0), line_status: it.line_status } })}><Edit fontSize="small" /></IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <Typography variant="subtitle2" sx={{ mt: 2 }}>Activity</Typography>
              {detail.events.map((ev: any) => (
                <Typography key={ev.id} variant="caption" display="block">
                  {fmtDateTime(ev.created_at)} · {EVENT_LABEL[ev.event_type] || ev.event_type}
                  {ev.from_value || ev.to_value ? `: ${ev.from_value ?? ''} → ${ev.to_value ?? ''}` : ''} · {ev.created_by}
                </Typography>
              ))}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => dl(detail, 'csv')} startIcon={<Description />}>CSV</Button>
              <Button onClick={() => dl(detail, 'pdf')} startIcon={<PictureAsPdf />}>PDF</Button>
              {['sent', 'partially_received'].includes(detail.status) && (
                <Button disabled={busy} startIcon={<Sync />} onClick={() => act(() => orderProcessorAPI.matchReceipts(detail.id), detail.id)}>Match receipts</Button>
              )}
              {['draft', 'sent', 'partially_received', 'received', 'closed'].includes(detail.status) && (
                <Button color="warning" startIcon={<Replay />} onClick={() => setReorderPo(detail.id)}>
                  {detail.status === 'draft' ? 'Move unavailable items' : 'Re-order missing items'}
                </Button>
              )}
              {detail.pending_customer_orders > 0 && detail.status !== 'cancelled' && (
                <Button color="secondary" disabled={busy}
                  onClick={() => {
                    if (!window.confirm(`Mark ${detail.pending_customer_orders} pending customer order(s) on ${detail.po_number} as Ordered? They will leave the Commitment page.`)) return;
                    act(() => orderProcessorAPI.markPoCustomerOrdersOrdered(detail.id), detail.id);
                  }}>
                  Mark {detail.pending_customer_orders} customer order(s) ordered
                </Button>
              )}
              <Box sx={{ flexGrow: 1 }} />
              {(PO_NEXT[detail.status] || []).map((s) => (
                <Button key={s} disabled={busy} variant={s === 'cancelled' ? 'outlined' : 'contained'} color={s === 'cancelled' ? 'error' : 'primary'}
                  onClick={() => {
                    if (s === 'cancelled' && !window.confirm(`Cancel ${detail.po_number} (${detail.distributor_name})? It can be reopened later from this dialog.`)) return;
                    act(() => orderProcessorAPI.setPurchaseOrderStatus(detail.id, s), detail.id);
                  }}>
                  {s === 'sent' ? 'Mark sent' : s === 'closed' ? 'Close PO' : s === 'cancelled' ? 'Cancel PO'
                    : s === 'draft' ? 'Reopen PO' : `Mark ${s.replace('_', ' ')}`}
                </Button>
              ))}
              <Button onClick={() => setDetail(null)}>Done</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <ReorderDialog poId={reorderPo} onClose={() => setReorderPo(null)} onDone={async (created) => {
        const src = reorderPo;
        setReorderPo(null);
        setNotice(`Created ${created.map((c: any) => c.po_number).join(', ')} (draft) — download and mark sent when ordered.`);
        await load();
        if (src) await openDetail(src);
      }} />
    </Box>
  );
};

export default PurchaseOrders;
